import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useImmer } from 'use-immer';
import * as Yup from 'yup';
import { EmployeeUserService } from '../../services';
import { Button, Card, Form, Col, Alert } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import FormControls from '../../controls/formControls';
import { Prompt, useParams } from 'react-router-dom';
import { BusyButton } from '../../controls/Buttons';

const employeeUserInitValues = {
    id: 0,
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: {
        number: '',
        phoneType: null
    },
    userType: 1,
    role: {},
    roleId: 0,
    accountId: 1,
    state: 100
}

const employeeUserValidation = Yup.object().shape({
    firstName: Yup.string().min(2, 'First name must be greater than two characters.').required('First name is required.'),
    lastName: Yup.string().min(2, 'Last name must be greater than two charcters.').required('Last name is required.'),
    emailAddress: Yup.string().email('Email address is not in the correct format').required('Email address is required.'),
    roleId: Yup.number().min(1, "Role is required.")
});

const EmployeeUser = (props) => {
    const [saveError, setSaveError] = useImmer('');
    const [employeeUser, setEmployeeUser] = useImmer(employeeUserInitValues);
    const [roles, setRoles] = useImmer([]);

    let { id } = useParams()

    useEffect(() => {
        ensureDataFetched();
    }, [])

    const ensureDataFetched = async () => {
        let userService = new EmployeeUserService();
        let callResult = await userService.getUser(id);
        if (callResult.result) {
            setEmployeeUser(draft => {
                Object.assign(draft, callResult.model.user);
            });
            setRoles(callResult.model.availableRoles);
        }
    }

    const formik = useFormik({
        initialValues: employeeUser,
        validationSchema: employeeUserValidation,
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting, setErrors, setStatus, resetForm }) => {
            setSaveError('');

            let userService = new EmployeeUserService();
            let returnVal = await userService.updateUser(values);

            if (!returnVal.result) {
                setSaveError(returnVal.message);
            } else {
                setEmployeeUser(returnVal.model);
            }
            setSubmitting(false);
        }
    });

    return (
        <>
            <Prompt
                when={formik.dirty}
                message="You have unsaved changes, are you sure you want to leave?"
            />
            <Card>
                <Card.Header className="d-flex align-items-center">
                    <div className="mr-auto" >{formik.values.firstName + ' ' + formik.values.lastName}</div>
                    <div className="card-btn-group">
                        <LinkContainer to={'/admin/users/'}>
                            <Button variant="secondary" size="sm">Close</Button>
                        </LinkContainer>
                        <FormControls.ConditionalRender visible={formik.dirty}>
                            <Button variant="secondary" size="sm" onClick={formik.handleReset}>Undo Changes</Button>
                            <BusyButton variant="success" size="sm" loading={formik.isSubmitting} onClick={() => formik.submitForm()}>Save</BusyButton>
                        </FormControls.ConditionalRender>

                    </div>
                </Card.Header>
                <Card.Body>
                    <Form.Row>
                        <Alert variant='danger' show={saveError}>{saveError}</Alert>
                        <Form.Group as={Col} lg="4" md="6">
                            <FormControls.Textbox
                                name="firstName"
                                label="First Name"
                                formHandler={formik} />
                        </Form.Group>
                        <Form.Group as={Col} lg="4" md="6">
                            <FormControls.Textbox
                                name="lastName"
                                label="Last Name"
                                formHandler={formik} />
                        </Form.Group>
                        <Form.Group as={Col} lg="4" md="6">
                            <FormControls.ButtonGrouping
                                label="State"
                                name="state"
                                size="sm"
                                variant="secondary"
                                formHandler={formik}
                                options={[
                                    { value: 100, name: "Active" },
                                    { value: -50, name: "Suspended" },
                                    { value: -100, name: "Deleted" }]} />
                        </Form.Group>
                    </Form.Row>
                    <Form.Row>
                        <Form.Group as={Col} lg="6" md="6">
                            <FormControls.Textbox
                                name="emailAddress"
                                label="Email Address"
                                type="email"
                                formHandler={formik} />
                        </Form.Group>
                        <Form.Group as={Col} lg="6" md="6">
                            <FormControls.SelectBox
                                label="Role"
                                name="roleId"
                                numeric
                                formHandler={formik}
                                defaultValue={0}
                                showDefault
                                defaultLabel="Select role ..."
                                value={formik.values?.roleId}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    formik.setFieldValue("roleId", val, true)
                                }}
                                options={roles}
                            />
                        </Form.Group>
                    </Form.Row>
                </Card.Body>
            </Card>
        </>
    )
}
export default EmployeeUser;